/**
 * The ways to change how the kiosk looks, for buttons, shortcuts, the dev panel and the console
 * (`window.kiosk.ui.apply('text')`). A preset change is animated: every module morphs to its new box
 * and the scene cross-fades (View Transitions API; without it the change is immediate).
 */
import { flushSync } from 'react-dom';
import { report } from '../core/diagnostics';
import { saveSettings, type Appearance } from '../core/appearance';
import { startingPreset, themeById } from '../themes/themes';
import type { AccessibilityMode } from '../core/wire';
import type { Preset } from '../layout/model';
import type { KioskApp } from '../react/app';

export interface UiActions {
  presets(): string[];
  /** Show this preset (and remember it on this device). */
  apply(presetId: string): void;
  /** Change theme: its look, its preset unless one is pinned by the URL, and its assistant's face; under a cloud. */
  setTheme(themeId: string): void;
  /** Try a preset not registered (the layout editor); null goes back to the chosen one. */
  preview(preset: Preset | null): void;
  /** The accessibility mode: through the backend when it has the button, else on this screen only. */
  setMode(mode: AccessibilityMode, options?: { localOnly?: boolean }): void;
  clearModeOverride(): void;
  togglePanel(panel: 'dev' | 'replay' | 'operator'): void;
  toggleModePicker(open?: boolean): void;
  toggleSettings(open?: boolean): void;
  /**
   * Change how the kiosk looks (Settings); saved on this device. With `close` it closes Settings in the
   * same step, as the choices that change the whole screen do (preset, theme, mode, reset): the change is
   * seen at once, and the page's transition never shows the sheet going and coming back. Without, Settings
   * stay open for the next choice (a detail of the chat, a part, a screen).
   */
  changeSettings(patch: Partial<Appearance>, options?: { close?: boolean }): void;
  /** Forget the Settings: back to the preset's own look. */
  resetSettings(): void;
  closeOverlay(): void;
  /** Start over: the session back to its start, and the language asked again (as on the old kiosk). */
  restart(): void;
  /** Leave this session for a new one: the page again, without `?session` (a new id). */
  newSession(): void;
}

interface ViewTransitionLike {
  ready?: Promise<unknown>;
  finished?: Promise<unknown>;
}

/** How long a change may wait for its animation before it is applied without one. */
const TRANSITION_GRACE_MS = 400;

/** Animations off: by Settings or a mode (the canvas's `data-motion`), or by the device. */
function reducedMotion(): boolean {
  return document.getElementById('kiosk-root')?.dataset.motion === 'reduce'
    || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
}

/** The cloud (overlays/Veil.tsx): how long it takes to pass, and when it covers the canvas, the change made unseen. */
export const VEIL_MS = 1100;
export const VEIL_PEAK_MS = 480;

/**
 * A change of the whole look (a theme): a cloud comes over the canvas, the change is made while it covers it, and it
 * goes, showing the new one. Without animations, at once.
 */
function veiled(app: KioskApp, update: () => void): void {
  if (reducedMotion()) {
    update();
    return;
  }
  const id = Date.now();
  app.ui.set((s) => ({ ...s, veil: id }));
  setTimeout(update, VEIL_PEAK_MS);
  setTimeout(() => app.ui.set((s) => (s.veil === id ? { ...s, veil: null } : s)), VEIL_MS);
}

function animated(app: KioskApp, update: () => void): void {
  const doc = document as Document & { startViewTransition?: (cb: () => void) => ViewTransitionLike };
  if (!doc.startViewTransition || reducedMotion()) {
    update();
    return;
  }
  // The animation is a nicety, never a wait: the browser runs the transition's callback only when it can
  // render a frame (late on a busy page, never on a hidden one), so the change is applied anyway after
  // TRANSITION_GRACE_MS, and whichever comes second does nothing.
  let applied = false;
  const apply = () => {
    if (applied) return;
    applied = true;
    flushSync(update);
  };
  setTimeout(apply, TRANSITION_GRACE_MS);
  try {
    const transition = doc.startViewTransition(apply);
    // A newer change skips this animation: its promises reject by design, the layout is still applied.
    transition.ready?.catch(() => undefined);
    transition.finished?.catch(() => undefined);
  } catch (e) {
    report('warn', 'shell', 'view transition failed, applied without animation', e);
    apply();
  }
  void app;
}

export function createUiActions(app: KioskApp): UiActions {
  const { ui } = app;
  return {
    presets: () => [...app.presets.keys()],
    apply(presetId) {
      if (!app.presets.has(presetId)) {
        report('warn', 'shell', `no preset "${presetId}" (have: ${[...app.presets.keys()].join(', ')})`);
        return;
      }
      const settings = { ...ui.get().settings, preset: presetId };
      animated(app, () => ui.set((s) => ({ ...s, presetId, draftPreset: null, settings, settingsOpen: false })));
      if (app.kiosk.role !== 'replay') saveSettings(settings);
    },
    setTheme(themeId) {
      const theme = themeById(themeId);
      const presetId = startingPreset({ theme, packDefault: app.pack.defaultPreset }, (id) => app.presets.has(id));
      // the preset, the face and the colours go with the theme: the new one's own apply
      const { preset: _preset, face: _face, faceFallback: _fallback, scheme: _scheme, ...rest } = ui.get().settings;
      const settings = { ...rest, theme: theme.id };
      ui.set((s) => ({ ...s, settingsOpen: false }));
      veiled(app, () => ui.set((s) => ({ ...s, presetId, draftPreset: null, settings })));
      if (app.kiosk.role !== 'replay') saveSettings(settings);
    },
    preview(preset) {
      animated(app, () => ui.set((s) => ({ ...s, draftPreset: preset })));
    },
    setMode(mode, options) {
      const button = app.pack.accessibilityButton;
      const session = app.kiosk.stores.session.get();
      const viaBackend = !options?.localOnly && button !== undefined && session?.valid_actions.includes(button);
      if (viaBackend) {
        void app.kiosk.press(button, { mode });
        animated(app, () => ui.set((s) => ({ ...s, modeOverride: null, modePickerOpen: false, settingsOpen: false })));
      } else {
        animated(app, () => ui.set((s) => ({ ...s, modeOverride: mode, modePickerOpen: false, settingsOpen: false })));
      }
    },
    clearModeOverride() {
      animated(app, () => ui.set((s) => ({ ...s, modeOverride: null })));
    },
    togglePanel(panel) {
      ui.set((s) => ({ ...s, panel: s.panel === panel ? 'none' : panel }));
    },
    toggleModePicker(open) {
      ui.set((s) => ({ ...s, modePickerOpen: open ?? !s.modePickerOpen }));
    },
    toggleSettings(open) {
      ui.set((s) => ({ ...s, settingsOpen: open ?? !s.settingsOpen }));
    },
    changeSettings(patch, options) {
      const settings = { ...ui.get().settings, ...patch };
      if (patch.widgetVariants) {
        // "auto" (an empty choice) means: let the preset and the pack decide
        settings.widgetVariants = Object.fromEntries(Object.entries(patch.widgetVariants).filter(([, v]) => v));
      }
      animated(app, () => ui.set((s) => ({ ...s, settings, settingsOpen: options?.close ? false : s.settingsOpen })));
      if (app.kiosk.role !== 'replay') saveSettings(settings);
    },
    newSession() {
      const url = new URL(window.location.href);
      url.searchParams.delete('session');
      window.location.assign(url);
    },
    resetSettings() {
      // the look goes back to the theme's and the preset's; the theme and the UI chosen stay
      const { theme, preset } = ui.get().settings;
      const settings = { ...(theme ? { theme } : {}), ...(preset ? { preset } : {}) };
      animated(app, () => ui.set((s) => ({ ...s, settings, settingsOpen: false })));
      if (app.kiosk.role !== 'replay') saveSettings(settings);
    },
    closeOverlay() {
      ui.set((s) => ({ ...s, overlay: null }));
    },
    restart() {
      ui.set((s) => ({ ...s, askLanguage: true, menu: null }));
      void app.kiosk.restart();
    },
  };
}
